import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node'

import { json, redirect } from '@remix-run/node'
import { isRouteErrorResponse, useLoaderData, useRouteError } from '@remix-run/react'
import Paragraph from '@sporza/design-system/atoms/paragraph'
import Title, { TitleSize } from '@sporza/design-system/atoms/title'
import { addAdUnit } from '@sporza/design-system/molecules/advertisement'
import { Page } from '@sporza/design-system/organisms/page'
import { useTrackingStore } from '@sporza/tracking'
import { isSamePath } from '@sporza/utils/urls'
import { useEffect } from 'react'

import { createPageTitle, getSeoMeta, toVersion2 } from '~/features/seo'
import { getEtag, setHeaders, withErrorHandling } from '~/helpers'
import bffClient from '~/services/bff'

export const headers: HeadersFunction = (
  {
    loaderHeaders,
    parentHeaders
  }
) => {
  const combinedHeaders = loaderHeaders

  if (loaderHeaders.get('etag') && parentHeaders.get('etag')) {
    combinedHeaders.set('etag', getEtag(
      {
        parentEtag: parentHeaders.get('etag'),
        loaderEtag: loaderHeaders.get('etag')
      }
    ))
  }

  return combinedHeaders
}

export const loader: LoaderFunction = withErrorHandling(async ({ request }) => {
  const requestUrl = new URL(request.url)
  const page = await bffClient.getInternalPage(requestUrl.pathname, 'aem', undefined, true)

  if (!page)
    throw new Response('Not Found', { status: 404 })

  if (page?.permalink && !isSamePath(page.permalink, requestUrl)) {
    const permalink = new URL(page.permalink)

    if (requestUrl.hash) {
      permalink.hash = requestUrl.hash
    }

    return redirect(permalink.toString(), 301)
  }

  const headers = setHeaders(page)

  return json({
    page,
    designSystemBaseUrl: process.env.DESIGN_SYSTEM_BASE_URL,
    apiBaseUrl: process.env.API_BASE_URL
  }, { headers })
})

export const meta: MetaFunction = ({ data }) => {
  const { page = {} } = data || {}
  const {
    title,
    seoTitle,
    description,
    structuredData,
    structuredDataLiveBlog,
    seoImage: image,
    seoPublicationDate: publicationDate,
    permalink
  } = page

  const seoMeta = getSeoMeta({
    title: createPageTitle(seoTitle || title),
    description
  })

  return toVersion2({
      ...seoMeta,
      image,
      publicationDate,
      permalink
    },
    Array.isArray(structuredData)
      ? structuredData
      : [
        structuredData,
        structuredDataLiveBlog
      ])
}

export default function Slug() {
  const data = useLoaderData<any>()
  const { page, designSystemBaseUrl, apiBaseUrl, bffBaseUrl  } = data
  const {
    digitalData,
    ebaData,
    adData
  } = page

  const initTracking = useTrackingStore((state) => state.initTracking)

  useEffect(() => {
    initTracking({ digitalData, ebaData })
    addAdUnit(adData)
  })

  return <Page
    designSystemBaseUrl={designSystemBaseUrl}
    apiBaseUrl={apiBaseUrl}
    bffBaseUrl={bffBaseUrl}
    {...page}
  />
}

export function ErrorBoundary() {
  const error = useRouteError()
  return <Page>
    <Title size={TitleSize.Large}>
      Ohoh, er is iets fout gegaan!
    </Title>

    <div style={{ '--paragraph-padding': '0 var(--spacing-12)' }}>
      <Paragraph>
        De pagina die je zocht werd niet gevonden.
      </Paragraph>

      <Paragraph>
        Ontdek al het <a href="/nl/" title="sporza.be">sportnieuws</a>!
      </Paragraph>
    </div>

    <div style={{ display: 'none' }}>
      {isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}`
        : error instanceof Error
          ? error.message
          : 'Unknown Error'}
    </div>
  </Page>
}
